import {
  DeliveryShipmentManifestReadinessIssueSchema,
  ShipmentManifestPayloadSnapshotSchema
} from "../chunk-3XEHZB66.js";

// compliance/license.schema.ts
import { z } from "zod";
var LICENSE_TYPES = [
  "cultivator",
  "dispensary",
  "manufacturer",
  "testing_lab",
  "transporter",
  "combined_use"
];
var LicenseTypeSchema = z.enum(LICENSE_TYPES);
var LICENSE_STATUSES = [
  "active",
  "expired",
  "suspended",
  "revoked",
  "pending_verification"
];
var LicenseStatusSchema = z.enum(LICENSE_STATUSES);
var LicenseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  licenseNumber: z.string(),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema,
  issuingState: z.string(),
  issuingAuthority: z.string().nullable(),
  issuedDate: z.date().nullable(),
  expirationDate: z.date().nullable(),
  lastVerifiedAt: z.date().nullable(),
  verifiedBy: z.string().uuid().nullable(),
  premisesName: z.string().nullable(),
  premisesAddress1: z.string(),
  premisesAddress2: z.string().nullable(),
  premisesCity: z.string(),
  premisesProvince: z.string(),
  premisesPostalCode: z.string(),
  premisesCountryCode: z.string(),
  stockLocationId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable()
});
var LicenseDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  licenseNumber: z.string(),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema,
  issuingState: z.string(),
  issuingAuthority: z.string().nullable(),
  issuedDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
  verifiedBy: z.string().nullable(),
  premisesName: z.string().nullable(),
  premisesAddress1: z.string(),
  premisesAddress2: z.string().nullable(),
  premisesCity: z.string(),
  premisesProvince: z.string(),
  premisesPostalCode: z.string(),
  premisesCountryCode: z.string(),
  stockLocationId: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var LicenseUpsertFieldsSchema = z.object({
  organizationId: z.string().uuid(),
  licenseNumber: z.string().min(1),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema.optional(),
  issuingState: z.string().optional(),
  issuingAuthority: z.string().nullable().optional(),
  issuedDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  premisesName: z.string().nullable().optional(),
  premisesAddress1: z.string().min(1),
  premisesAddress2: z.string().nullable().optional(),
  premisesCity: z.string().min(1),
  premisesProvince: z.string().min(1),
  premisesPostalCode: z.string().min(1),
  premisesCountryCode: z.string().optional(),
  stockLocationId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var CreateLicenseDtoSchema = LicenseUpsertFieldsSchema;
var UpdateLicenseDtoSchema = LicenseUpsertFieldsSchema.partial();
var LicenseFiltersSchema = z.object({
  organizationId: z.string().uuid().optional(),
  status: LicenseStatusSchema.optional(),
  licenseType: LicenseTypeSchema.optional()
});

// compliance/transfer-template.schema.ts
import { z as z2 } from "zod";
var TRANSFER_TEMPLATE_STATUSES = ["draft", "ready", "submitted", "voided"];
var TransferTemplateStatusSchema = z2.enum(TRANSFER_TEMPLATE_STATUSES);
var TRANSFER_TEMPLATE_LAYOVER_ROLES = [
  "ToLayover",
  "FromLayover",
  "FromAndToLayover"
];
var TransferTemplateLayoverRoleSchema = z2.enum(TRANSFER_TEMPLATE_LAYOVER_ROLES);
var TransferTemplateLayoverLegInputSchema = z2.object({
  driverId: z2.string().uuid(),
  role: TransferTemplateLayoverRoleSchema,
  estimatedDepartureAt: z2.string().optional(),
  estimatedArrivalAt: z2.string().optional()
});
var TransferTemplateLocalLayoverAuditInputSchema = z2.object({
  stopLocationName: z2.string().min(1).optional(),
  stopLicenseNumber: z2.string().min(1).optional(),
  stopLicenseId: z2.string().uuid().optional(),
  notes: z2.string().max(1e3).optional()
});
var TransferTemplateReadinessSchema = z2.object({
  isReady: z2.boolean(),
  blockingErrors: z2.array(DeliveryShipmentManifestReadinessIssueSchema),
  warnings: z2.array(DeliveryShipmentManifestReadinessIssueSchema),
  info: z2.array(DeliveryShipmentManifestReadinessIssueSchema).default([]),
  checkedAt: z2.date(),
  metadata: z2.record(z2.unknown()).default({})
});
var TransferTemplateSchema = z2.object({
  id: z2.string().uuid(),
  partitionId: z2.string().uuid(),
  organizationId: z2.string().uuid(),
  status: TransferTemplateStatusSchema,
  version: z2.number().int().positive(),
  metrcTransferId: z2.number().int().positive().nullable(),
  metrcManifestNumber: z2.string().nullable(),
  shipperLicenseNumber: z2.string().nullable(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.nullable(),
  lastPreparedAt: z2.date().nullable(),
  submittedAt: z2.date().nullable(),
  voidedAt: z2.date().nullable(),
  metadata: z2.record(z2.unknown()),
  createdAt: z2.date(),
  updatedAt: z2.date()
});
var CreateTransferTemplateSchema = z2.object({
  partitionId: z2.string().uuid(),
  organizationId: z2.string().uuid(),
  status: TransferTemplateStatusSchema.default("draft"),
  version: z2.number().int().positive().default(1),
  metrcTransferId: z2.number().int().positive().nullable().optional(),
  metrcManifestNumber: z2.string().nullable().optional(),
  shipperLicenseNumber: z2.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.nullable().optional(),
  lastPreparedAt: z2.date().nullable().optional(),
  submittedAt: z2.date().nullable().optional(),
  voidedAt: z2.date().nullable().optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var UpdateTransferTemplateSchema = z2.object({
  status: TransferTemplateStatusSchema.optional(),
  version: z2.number().int().positive().optional(),
  metrcTransferId: z2.number().int().positive().nullable().optional(),
  metrcManifestNumber: z2.string().nullable().optional(),
  shipperLicenseNumber: z2.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema.optional(),
  readinessSnapshot: TransferTemplateReadinessSchema.nullable().optional(),
  lastPreparedAt: z2.date().nullable().optional(),
  submittedAt: z2.date().nullable().optional(),
  voidedAt: z2.date().nullable().optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var TransferTemplateDtoSchema = z2.object({
  id: z2.string().uuid(),
  partitionId: z2.string().uuid(),
  organizationId: z2.string().uuid(),
  status: TransferTemplateStatusSchema,
  version: z2.number().int().positive(),
  metrcTransferId: z2.number().int().positive().nullable(),
  metrcManifestNumber: z2.string().nullable(),
  shipperLicenseNumber: z2.string().nullable(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.extend({
    checkedAt: z2.string()
  }).nullable(),
  lastPreparedAt: z2.string().nullable(),
  submittedAt: z2.string().nullable(),
  voidedAt: z2.string().nullable(),
  metadata: z2.record(z2.unknown()),
  createdAt: z2.string(),
  updatedAt: z2.string()
});
var TransferTemplateReadinessDtoSchema = TransferTemplateReadinessSchema.extend({
  checkedAt: z2.string()
});
var TransferTemplateBatchStatusItemSchema = z2.object({
  partitionId: z2.string().uuid(),
  status: TransferTemplateStatusSchema,
  driverName: z2.string().nullable(),
  metrcManifestNumber: z2.string().nullable(),
  submittedAt: z2.string().nullable(),
  estimatedDepartureAt: z2.string().nullable()
});
export {
  CreateLicenseDtoSchema,
  CreateTransferTemplateSchema,
  LICENSE_STATUSES,
  LICENSE_TYPES,
  LicenseDtoSchema,
  LicenseFiltersSchema,
  LicenseSchema,
  LicenseStatusSchema,
  LicenseTypeSchema,
  TRANSFER_TEMPLATE_LAYOVER_ROLES,
  TRANSFER_TEMPLATE_STATUSES,
  TransferTemplateBatchStatusItemSchema,
  TransferTemplateDtoSchema,
  TransferTemplateLayoverLegInputSchema,
  TransferTemplateLayoverRoleSchema,
  TransferTemplateLocalLayoverAuditInputSchema,
  TransferTemplateReadinessDtoSchema,
  TransferTemplateReadinessSchema,
  TransferTemplateSchema,
  TransferTemplateStatusSchema,
  UpdateLicenseDtoSchema,
  UpdateTransferTemplateSchema
};
